import {IsInView} from '../globals/is-in-view';
import {DraggableSlider} from '../features/draggable-slider';

if (!customElements.get('grid-slider')) {
  customElements.define(
    'grid-slider',

    class GridSlider extends HTMLElement {
      constructor() {
        super();

        this.isInitialized = false;
        this.draggableSlider = null;
        this.positionArrows = this.positionArrows.bind(this);
        this.onButtonArrowClick = (e) => this.buttonArrowClickEvent(e);
        this.slidesObserver = null;
        this.firstLastSlidesObserver = null;
        this.isDragging = false;
        this.toggleSlider = this.toggleSlider.bind(this);
      }

      connectedCallback() {
        this.init();
        this.addEventListener('theme:grid-slider:init', this.init);
      }

      init() {
        this.slider = this.querySelector('[data-grid-slider]');
        this.slides = this.querySelectorAll('[data-grid-item]');
        this.buttons = this.querySelectorAll('[data-button-arrow]');
        this.slider.classList.add('scroll-snap-disabled');
        this.toggleSlider();
        document.addEventListener('theme:resize:width', this.toggleSlider);

        window.theme
          .waitForAllAnimationsEnd(this)
          .then(() => {
            this.slider.classList.remove('scroll-snap-disabled');
          })
          .catch(() => {
            this.slider.classList.remove('scroll-snap-disabled');
          });
      }

      toggleSlider() {
        const sliderWidth = this.slider.clientWidth;
        const slidesWidth = this.getSlidesWidth();
        const isEnabled = sliderWidth < slidesWidth;

        if (isEnabled && (!theme.isMobile || !window.theme.touch)) {
          if (this.isInitialized) return;

          this.slidesObserver = new IsInView(this.slider, '[data-grid-item]');

          this.initArrows();
          this.isInitialized = true;

          // Create an instance of DraggableSlider
          this.draggableSlider = new DraggableSlider(this.slider);
        } else {
          this.destroy();
        }
      }

      initArrows() {
        // Create arrow buttons if don't exist
        if (!this.buttons.length) {
          const buttonsWrap = document.createElement('div');
          buttonsWrap.classList.add('slider__arrows');
          buttonsWrap.innerHTML = theme.sliderArrows.prev + theme.sliderArrows.next;

          // Append buttons outside the slider element
          this.append(buttonsWrap);
          this.buttons = this.querySelectorAll('[data-button-arrow]');
          this.buttonPrev = this.querySelector('[data-button-prev]');
          this.buttonNext = this.querySelector('[data-button-next]');
        }

        this.toggleArrowsObserver();

        if (this.hasAttribute('align-arrows')) {
          this.positionArrows();
          this.arrowsResizeObserver();
        }

        this.buttons.forEach((buttonArrow) => {
          buttonArrow.addEventListener('click', this.onButtonArrowClick);
        });
      }

      buttonArrowClickEvent(e) {
        e.preventDefault();

        const firstVisibleSlide = this.slider.querySelector(`[data-grid-item].is-visible`);
        let slide = null;

        if (e.target.hasAttribute('data-button-prev')) {
          slide = firstVisibleSlide?.previousElementSibling;
        }

        if (e.target.hasAttribute('data-button-next')) {
          slide = firstVisibleSlide?.nextElementSibling;
        }

        this.goToSlide(slide);
      }

      removeArrows() {
        this.querySelector('.slider__arrows')?.remove();
      }

      // Go to prev/next slide on arrow click
      goToSlide(slide) {
        if (!slide) return;

        this.slider.scrollTo({
          top: 0,
          left: slide.offsetLeft,
          behavior: 'smooth',
        });
      }

      getSlidesWidth() {
        return this.slider.querySelector('[data-grid-item]')?.clientWidth * this.slider.querySelectorAll('[data-grid-item]').length;
      }

      toggleArrowsObserver() {
        // Add disable class/attribute on prev/next button

        if (this.buttonPrev && this.buttonNext) {
          const slidesCount = this.slides.length;
          const firstSlide = this.slides[0];
          const lastSlide = this.slides[slidesCount - 1];

          const config = {
            attributes: true,
            childList: false,
            subtree: false,
          };

          const callback = (mutationList) => {
            for (const mutation of mutationList) {
              if (mutation.type === 'attributes') {
                const slide = mutation.target;
                const isDisabled = Boolean(slide.classList.contains('is-visible'));

                if (slide == firstSlide) {
                  this.buttonPrev.disabled = isDisabled;
                }

                if (slide == lastSlide) {
                  this.buttonNext.disabled = isDisabled;
                }
              }
            }
          };

          if (firstSlide && lastSlide) {
            this.firstLastSlidesObserver = new MutationObserver(callback);
            this.firstLastSlidesObserver.observe(firstSlide, config);
            this.firstLastSlidesObserver.observe(lastSlide, config);
          }
        }
      }

      positionArrows() {
        if (this.hasAttribute('images-widths-different')) {
          const figureElements = this.slider.querySelectorAll('figure');

          const biggestHeight = Math.max(0, ...Array.from(figureElements).map((figure) => figure.clientHeight));

          this.style.setProperty('--button-position', `${biggestHeight / 2}px`);
          return;
        }

        const targetElement =
          this.slider.querySelector('[data-social-video-item]') ||
          this.slider.querySelector('[data-product-image]') ||
          this.slider.querySelector('[data-collection-image]') ||
          this.slider.querySelector('[data-column-image]') ||
          this.slider;

        if (!targetElement) return;

        this.style.setProperty('--button-position', `${targetElement.clientHeight / 2}px`);
      }

      arrowsResizeObserver() {
        document.addEventListener('theme:resize:width', this.positionArrows);
      }

      disconnectedCallback() {
        this.destroy();
        document.removeEventListener('theme:resize:width', this.toggleSlider);
      }

      destroy() {
        this.isInitialized = false;
        this.draggableSlider?.destroy();
        this.draggableSlider = null;
        this.slidesObserver?.destroy();
        this.slidesObserver = null;
        this.removeArrows();

        document.removeEventListener('theme:resize:width', this.positionArrows);
      }
    }
  );
}
